import { useEffect, useState } from "react";
import { FaBook, FaPlus, FaPen, FaArchive, FaThumbtack, FaPaperclip, FaCodeBranch, FaEye, FaTasks, FaSlidersH, FaChartBar } from "react-icons/fa";
import { LOGBOOK_CONSTANTS } from "../_MODULES/_API";
import { LogbookFormModal } from "./LogbookFormModal";
import { STATUS_MAP_LOGBOOK, STATUS_MAP_PRIORITY, OPTIONS_LOGBOOK_TYPE, OPTIONS_LOGBOOK_STATUS, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, EmptyState, Skeleton, Chip, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { onApiAction } from "../../../../_UI/onApiAction";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { LogbookReadModal } from "./LogbookReadModal";

export const PanelLogbook = ({ subProjectId }) => {

    const [useSearch, setSearch] = useState("");
    const [useType, setType] = useState("");
    const [useStatus, setStatus] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [useEntryEdit, setEntryEdit] = useState(null);
    const [useEntryRead, setEntryRead] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);

    const { apiGet: onGetLogbook, useApiData: ENTRIES, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    const onLoadLogbook = () => onGetLogbook({
        endpoint: LOGBOOK_CONSTANTS.LIST,
        config: {
            params: {
                subProjectId,
                search: useSearch || undefined,
                type: useType || undefined,
                status: useStatus || undefined,
            },
        },
    });

    useEffect(() => {
        const TIMER = setTimeout(onLoadLogbook, 250);
        return () => clearTimeout(TIMER);
    }, [subProjectId, useSearch, useType, useStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenCreate = () => {
        setEntryEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (entry) => {
        setEntryEdit(entry);
        setIsModalOpen(true);
    };

    const onTogglePin = async (entry) => {
        const NEXT = !entry.isPinned;
        mutateLocal({ id: entry.id, patch: { isPinned: NEXT } });

        const RESPONSE = await actionMutate({
            method: "PATCH", endpoint: LOGBOOK_CONSTANTS.UPDATE, successMessage: "Entrada actualizada", id: entry.id,
            data: { isPinned: NEXT },
        });

        if (!RESPONSE.success) mutateLocal({ id: entry.id, patch: { isPinned: entry.isPinned } });
    };

    const onArchive = async (entry) => {

        const IS_CONFIRMED = await confirm.danger("Archivar entrada", `Se archiva "${entry.title}" con sus tareas, adjuntos y cambios. La podés recuperar desde Archivados.`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: LOGBOOK_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: entry.id }, successMessage: "Entrada archivada",
        });

        if (RESPONSE.success) mutateLocal({ id: entry.id, remove: true });
    };

    const SORTED_ENTRIES = [...(ENTRIES ?? [])].sort((a, b) =>
        (Number(!!b.isPinned) - Number(!!a.isPinned)) || String(b.updatedAt ?? "").localeCompare(String(a.updatedAt ?? ""))
    );
    const PAGER = usePagedList(SORTED_ENTRIES, 6, "logbookPage");

    const TYPE_OPTIONS = [{ value: "", label: "Todos los tipos" }, ...OPTIONS_LOGBOOK_TYPE];
    const STATUS_OPTIONS = [{ value: "", label: "Todos los estados" }, ...OPTIONS_LOGBOOK_STATUS];

    return (
        <>
            <div className="LStudio__app_panel_bar">

                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} placeholder="Ej.: Error 500, deploy o migración..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {(useType || useStatus) ? `Avanzado (${Number(!!useType) + Number(!!useStatus)})` : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen}
                        activeCount={Number(!!useType) + Number(!!useStatus)} onClear={() => { setType(""); setStatus(""); }}>
                        <FieldSelect id="logbook_type" label="Tipo" options={TYPE_OPTIONS} isSearchable
                            value={useType} onChange={(event) => setType(event.target.value)} />
                        <FieldSelect id="logbook_status" label="Estado" options={STATUS_OPTIONS} isSearchable
                            value={useStatus} onChange={(event) => setStatus(event.target.value)} />
                    </AdvancedFilterPanel>}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Título" },
                        { key: "type", label: "Tipo" },
                        { key: "status", label: "Estado" },
                        { key: "priority", label: "Prioridad" },
                        { key: "specification", label: "Especificación" },
                        { key: "problem", label: "Problema" },
                        { key: "solution", label: "Solución" },
                    ]}
                    exportRows={(ENTRIES ?? []).map((entry) => ({
                        title: entry.title,
                        type: onFindOptionLabel(OPTIONS_LOGBOOK_TYPE, entry.type),
                        status: onFindOptionLabel(OPTIONS_LOGBOOK_STATUS, entry.status),
                        priority: entry.priority,
                        specification: entry.specification ?? "",
                        problem: entry.problem ?? "",
                        solution: entry.solution ?? "",
                    }))}
                    exportName="logbook"
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly
                            title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nueva entrada</AppButton>
                    </>} />

            </div>

            <p className="LStudio__app_todos_hint LStudio__app_logbook_hint">
                <strong>Bitácora</strong> es el historial del trabajo: guardá qué pasó, un problema, una decisión, una solución o código importante.
                Podés asociarle tareas, pero no es obligatorio. Para un pendiente simple usá <strong>Tareas → Tareas sueltas</strong>.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={180} count={2} />}

            {
                !IS_LOADING && (ENTRIES?.length ?? 0) === 0 && (
                    <EmptyState icon={<FaBook />} title="La bitácora está vacía"
                        description="Anotá acá lo que hacés, los problemas que te aparecen y cómo los resolviste."
                        action={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nueva entrada</AppButton>} />
                )
            }

            {
                !IS_LOADING && ENTRIES?.length > 0 && (
                    <div className="LStudio__app_logbook_list">
                        {
                            PAGER.paged.map((entry) => {
                                const PREVIEW_SECTIONS = [
                                    { id: "specification", label: "Especificación", value: entry.specification },
                                    { id: "problem", label: "Problema", value: entry.problem },
                                    { id: "solution", label: "Solución", value: entry.solution },
                                    { id: "notes", label: "Notas", value: entry.content },
                                ].filter((section) => String(section.value ?? "").trim());

                                const TOTAL_TODOS = entry.todos?.length ?? 0;
                                const DONE_TODOS = entry.todos?.filter((todo) => todo.isDone)?.length ?? 0;
                                const TOTAL_CHANGES = entry.changes?.length ?? 0;
                                const TOTAL_FILES = entry.documents?.length ?? 0;

                                return (
                                    <Card key={entry.id} className="LStudio__app_logbook_item">
                                        <div className="LStudio__app_logbook_item_head">
                                            <h3 className="LStudio__app_logbook_item_title">
                                                {entry.isPinned && <FaThumbtack aria-hidden="true" />}
                                                {entry.title}
                                            </h3>

                                            <Badge tone="neutral" size="sm">{onFindOptionLabel(OPTIONS_LOGBOOK_TYPE, entry.type)}</Badge>
                                            <StatusBadge status={entry.status} map={STATUS_MAP_LOGBOOK} size="sm" />
                                            <StatusBadge status={entry.priority} map={STATUS_MAP_PRIORITY} size="sm" variant="outline" />
                                        </div>

                                        {PREVIEW_SECTIONS.length > 0 && (
                                            <div className="LStudio__app_logbook_item_preview_grid">
                                                {PREVIEW_SECTIONS.map((section) => (
                                                    <div key={section.id} className="LStudio__app_logbook_item_block">
                                                        <span className="LStudio__app_logbook_item_label">{section.label}</span>
                                                        <p className="LStudio__app_logbook_item_preview_text">{section.value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {entry.code && (
                                            <div className="LStudio__app_logbook_item_code_preview">
                                                <AppCodeBlock
                                                    code={entry.code}
                                                    language={entry.codeLanguage}
                                                    title={`Código (${entry.codeLanguage || "text"})`}
                                                    isPretty={false}
                                                    maxLines={4}
                                                />
                                            </div>
                                        )}

                                        {entry.tags?.length > 0 && (
                                            <div className="LStudio__app_logbook_item_tags">
                                                {entry.tags.slice(0, 6).map((tag) => (
                                                    <Chip key={tag.id} tone="accent">{tag.name}</Chip>
                                                ))}
                                                {entry.tags.length > 6 && (
                                                    <Badge tone="neutral" size="sm">+{entry.tags.length - 6}</Badge>
                                                )}
                                            </div>
                                        )}

                                        {(TOTAL_CHANGES > 0 || TOTAL_TODOS > 0 || TOTAL_FILES > 0) && (
                                            <div className="LStudio__app_logbook_item_summary">
                                                {TOTAL_CHANGES > 0 && (
                                                    <span><FaCodeBranch /> {TOTAL_CHANGES} {TOTAL_CHANGES === 1 ? "cambio" : "cambios"}</span>
                                                )}
                                                {TOTAL_TODOS > 0 && (
                                                    <span><FaTasks /> {DONE_TODOS}/{TOTAL_TODOS} tareas</span>
                                                )}
                                                {TOTAL_FILES > 0 && (
                                                    <span><FaPaperclip /> {TOTAL_FILES} {TOTAL_FILES === 1 ? "archivo" : "archivos"}</span>
                                                )}
                                            </div>
                                        )}

                                        <div className="LStudio__app_logbook_item_actions">
                                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEye />}
                                                onClick={() => setEntryRead(entry)}>Ver completo</AppButton>

                                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaThumbtack />}
                                                onClick={() => onTogglePin(entry)}>
                                                {entry.isPinned ? "Quitar fijado" : "Fijar"}
                                            </AppButton>

                                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPen />}
                                                onClick={() => onOpenEdit(entry)}>Editar</AppButton>

                                            <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                                onClick={() => onArchive(entry)}>Archivar</AppButton>
                                        </div>
                                    </Card>
                                );
                            })
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />


            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Bitácora" description="Resumen del historial documentado en este subproyecto."
                metrics={[
                    { key: "entries", label: "Entradas", value: SORTED_ENTRIES.length, icon: <FaBook />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "pinned", label: "Fijadas", value: SORTED_ENTRIES.filter((entry) => entry.isPinned).length, icon: <FaThumbtack />, tone: "accent", isLoading: IS_LOADING },
                    { key: "tasks", label: "Tareas vinculadas", value: SORTED_ENTRIES.reduce((total, entry) => total + (entry.todos?.length ?? 0), 0), icon: <FaTasks />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "changes", label: "Cambios", value: SORTED_ENTRIES.reduce((total, entry) => total + (entry.changes?.length ?? 0), 0), icon: <FaCodeBranch />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "files", label: "Archivos", value: SORTED_ENTRIES.reduce((total, entry) => total + (entry.documents?.length ?? 0), 0), icon: <FaPaperclip />, tone: "neutral", isLoading: IS_LOADING },
                ]} />

            <LogbookReadModal
                isOpen={!!useEntryRead}
                entry={useEntryRead}
                onClose={() => setEntryRead(null)}
            />

            <LogbookFormModal
                isOpen={useIsModalOpen}
                subProjectId={subProjectId}
                entry={useEntryEdit}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => mutateLocal(isEdit
                    ? { id: saved.id, patch: saved }
                    : { insert: saved })} />
        </>
    );
};
